<template>
    <section id="contact-section" class="text-center" :class="context.isDarkMode ? 'bg-semidark-silver' : 'bg-white'">
        <div class="mx-5 md:mx-10 lg:mx-20 xl:mx-40 flex flex-col lg:flex-row">
            <div class="w-full text-center lg:text-left lg:w-1/2 flex flex-col justify-center text-left lg:mb-5">
                <div class="w-full lg:w-5/6 2xl:w-2/3" :class="context.isDarkMode ? 'text-silver' : 'text-void'">
                    <h2 class="text-2xl font-bold translate-text">
                        {{ translationHandler.translate('contact us', 'ucfirst') }}
                    </h2>
                    <p class="mt-4 text-lg translate-text">
                        {{ translationHandler.translate('this section is your direct channel to connect with us.', 'ucfirst') }}
                    </p>

                    <p class="text-lg my-5 font-semibold" :class="context.isDarkMode ? 'text-first-color' : 'text-third-color'">
                        {{ translationHandler.translate('or if you prefer:', 'ucfirst') }}
                    </p>
                    <p class="text-lg">
                        {{ translationHandler.translate("click below to access our 'Automated Estimate with Bot' and start a conversation with our artificial intelligence to get a fast, personalized estimate for your project", 'ucfirst') }}
                    </p>

                    <div class="flex gap-2 content-center primary-button max-width-md grow-effect rounded-3xl px-4 shadow mt-10 mx-auto lg:mx-0 lg:mt-5" @click="urlHandler.redirect('/budget')">
                        <span class="text-lg lg:text-lg 2xl:text-2xl my-auto text-silver font-semibold mx-auto md:mx-0">
                            {{ translationHandler.translate('auto budget', 'capitalize') }}
                        </span>
                        <i class="text-silver pi pi-microchip-ai border border-second-color rounded-full bg-second-color p-2 my-auto shadow ml-auto"></i>
                    </div>
                </div>
            </div>

            <div class="w-full mt-10 md:px-20 lg:mt-0 lg:w-4/5 2xl:w-2/3 flex">
                <form 
                    id="contact-form" 
                    class="mt-6 mx-auto w-full md:w-4/5 lg:w-4/5 xl:w-4/5 p-6 rounded-2xl border"
                    :class="context.isDarkMode ? 'bg-void border-semidark-silver' : 'bg-white border-light-white shadow text-void'"
                    style="margin-bottom: -5rem;"
                >
                    <InputText type="text" :class="'w-full p-2 mb-4 shadow-lg rounded'"
                        :placeholder="translationHandler.translate('your name*', 'capitalize')"
                        v-model="formEmail.name" />

                    <InputText type="email" :class="'w-full p-2 mb-4 shadow-lg rounded'"
                        :placeholder="translationHandler.translate('your email*', 'capitalize')"
                        v-model="formEmail.email" />

                    <div class="w-full mb-4">
                        <PhoneInput v-model="formEmail.phoneNumber" @selectedCountry="onCountryChange" :hasPhoneValue="formEmail.phoneNumber.length > 0 ? true : false" />
                    </div>

                    <div class="w-full py-2 mb-4 text-left">
                        <InputText type="text" :class="'w-full shadow-lg rounded'"
                            :placeholder="translationHandler.translate('title*', 'capitalize')"
                            v-model="formEmail.title" @input="setTitleSize()" />
                        <small class="mr-auto w-full" :class="context.isDarkMode ? 'text-white' : 'text-void'">{{ titleLength }} {{ translationHandler.translate('of') }}
                            250</small>
                    </div>

                    <div class="w-full py-2 mb-4 text-left">
                        <Textarea v-model="formEmail.message"
                            :placeholder="translationHandler.translate('your message*', 'capitalize')" rows="5" cols="30"
                            autoResize
                            style="width: 100% !important;"
                            @input="setTextAreaSize(conversation)" />
                        <small class="mr-auto w-full" :class="context.isDarkMode ? 'text-white' : 'text-void'">{{ textAreaSize }} {{ translationHandler.translate('of') }}
                            4000</small>
                    </div>

                    <div 
                        class="flex gap-2 content-center mt-10 primary-button max-width-md grow-effect rounded-3xl px-4 shadow"
                        @click.prevent="saveSimpleBudget()"
                    >
                        <span class="text-lg lg:text-lg 2xl:text-2xl my-auto text-silver font-semibold mx-auto md:mx-0">
                            {{ translationHandler.translate('send', 'ucfirst') }}
                        </span>
                        <i class="ml-auto text-silver pi pi-send border border-second-color rounded-full bg-second-color p-2 my-auto shadow"></i>
                    </div>
                </form>
            </div>
        </div>
    </section>
</template>

<script setup>
import { reactive, ref, defineEmits } from 'vue';
import translationHandler from '@/composables/handlers/TranslationHandler';
import { InputText, Textarea, useToast } from 'primevue';
import PhoneInput from '@/components/Utils/PhoneComponent.vue';
import { showToast } from '@/composables/toast';
import { useContextStore } from '@/stores/context';
import urlHandler from '@/composables/urlHandler';
import BudgetService from '@/services/BudgetService';
import utils from '@/composables/utils';

const context = useContextStore();
const emit = defineEmits(['openLoader', 'showSuportModal']);

const toast = useToast();
const formEmail = reactive({
    name: '',
    email: '',
    phoneNumber: '',
    title: '',
    message: '',
    selected_country: ''
});

const lastTextAreaValue = ref('');
const textAreaSize = ref(0);
function setTextAreaSize() {
    let value = formEmail.message;
    if (!value) {
        textAreaSize.value = 0;
        lastTextAreaValue.value = '';
        return;
    }

    if (value.length > 4000) {
        formEmail.message = value.slice(0, 4000);
        showToast(toast, translationHandler.translate('maximum limit of 4000 characters reached.', 'ucfirst'), 'info');
    }

    lastTextAreaValue.value = formEmail.message;
    textAreaSize.value = lastTextAreaValue.value.length;
}

const lastTitleValue = ref('');
const titleLength = ref(0);
function setTitleSize() {
    let value = formEmail.title;
    if (!value) {
        titleLength.value = 0;
        lastTitleValue.value = '';
        return;
    }

    if (value.length > 250) {
        formEmail.message = value.slice(0, 250);
        showToast(toast, translationHandler.translate('maximum limit of 250 characters reached.', 'ucfirst'), 'info');
    }

    lastTitleValue.value = formEmail.title;
    titleLength.value = lastTitleValue.value.length;
}

async function saveSimpleBudget() {
    emit('openLoader', true);

    const itemsToCheck = [
        {name: 'name', rule: 'name', required: true},
        {name: 'email', rule: 'email', required: true},
        {name: 'phoneNumber', rule: 'phone', required: false},
        {name: 'title', rule: 'title', required: true},
        {name: 'message', rule: 'description', required: true},
    ];
    for (let item of itemsToCheck) {
        const checkResult = await checkFields(item.rule, formEmail[item.name]);
        if (
            !checkResult.valid &&
            (
                ![null, undefined, ''].includes(formEmail[item.name]) ||
                item.required == true
            )
        ) {
            showToast(toast, translationHandler.translate(checkResult.message, 'ucfirst'), 'error');
            emit('openLoader', false);
            return;
        }
    }
    
    const response = await BudgetService.saveBudget(formEmail, true);
    const messages = {
        success: 'budget sent, thank you',
        error: 'budget not sent, try again later or contact the support'
    }

    const translatedMessage = translationHandler.translate(messages[response ? 'success' : 'error'], 'ucfirst');
    
    emit('openLoader', false);
    showToast(toast, translatedMessage, response ? 'success' : 'error');
    
    if (!response) {
        emit('showSuportModal', {data: utils.copyVariable(formEmail), action: 'BudgetService.saveSimpleBudget', component: 'ContactComponent'});
    }

    resetFormEmail();
}

function onCountryChange(country) {
    if (country.code) {
        formEmail.selected_country = country.code;
    }
}

function resetFormEmail() {
    formEmail.name = '';
    formEmail.email = '';
    formEmail.phoneNumber = '';
    formEmail.title = '';
    formEmail.message = '';
    
    textAreaSize.value = 0;
    titleLength.value = 0;
}

async function checkFields(rule, value) {
        let data = {
            valid: true,
            message: null
        };
        
        switch (rule) {
            case 'email':
                data.valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
                data.message = 'email is invalid';
                break;
            case 'phone':
                data.valid = /^\+?\d{10,15}$/.test(value.replace(/\s+/g, ''));
                data.message = 'telephone number is invalid';
                break;
            case 'title':
                data.valid = value.length > 20;
                data.message = 'the title is too short, 20 letters are required at least';
                break;
            case 'description':
                data.valid = value.length > 50;
                data.message = 'the description is too short, 50 letters are required at least';
                break;
            case 'text-area':
                data.valid = value.length > 50;
                data.message = 'tell us more about your idea, 50 characters at least';
                break;
            case 'name':
                data.valid = value.split(' ').length == 1 || value.length < 6 ? false : true;
                data.message = 'your full name is required';
                break;
            case 'url':
                if (value && value.length > 0){
                    data.valid = await urlHandler.isUrlValid(value);
                    data.message = !data.valid ? 'invalid url' : '';
                } else {
                    data.valid = true;
                }
                break;
            default:
                data.valid = true;
                break;
        }

        return data;
    }

</script>